import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../contexts/SidebarContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useNotification } from '../../contexts/NotificationContext';
import Header from '../../components/ui/Header';
import EnhancedSidebar from '../../components/ui/EnhancedSidebar';
import EnhancedBreadcrumb from '../../components/ui/EnhancedBreadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CRMPipelineComponent from './components/CRMPipeline';
import StageConfigModal from './components/StageConfigModal';
import AddCustomerModal from './components/AddCustomerModal';
import EditCustomerModal from './components/EditCustomerModal';
import DeleteContactModal from '../clients/components/DeleteContactModal';
import CustomerDetailModal from '../clients/components/CustomerDetailModal';
import customerService from '../../utils/customerService';

const CRMPage = () => {
  const navigate = useNavigate();
  const { sidebarExpanded, handleSidebarToggle } = useSidebar();
  const { currentOrganization } = useOrganization();
  const { addNotification } = useNotification();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showStageConfig, setShowStageConfig] = useState(false);
  const [stageRefreshKey, setStageRefreshKey] = useState(0);
  const [newCustomerStage, setNewCustomerStage] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [customerDetailModalOpen, setCustomerDetailModalOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    archived: 0,
    individuals: 0,
    organizations: 0,
    totalEstimatedValue: 0,
    byStage: {}
  });

  // Load customers from database (CRM optimized)
  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await customerService.getCRMPipelineData({ status: 'active' });
      setCustomers(data);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load customer statistics (CRM optimized)
  const loadStats = async () => {
    try {
      const statsData = await customerService.getCRMPipelineStats(currentOrganization?.id);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  // Initial data load
  useEffect(() => {
    if (currentOrganization?.id) {
      loadCustomers();
      loadStats();
    }
  }, [currentOrganization?.id]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleAddCustomer = async (newCustomerData) => {
    try {
      await loadCustomers();
      await loadStats();
      setNewCustomerStage(null);
      setShowAddModal(false);
    } catch (err) {
      console.error('Error refreshing customer data:', err);
      setError('Failed to refresh customer data. Please try again.');
    }
  };

  const handleEditCustomer = (customer) => {
    setCustomerToEdit(customer);
    setEditModalOpen(true);
  };

  const handleUpdateCustomer = async (updatedCustomerData) => {
    try {
      await customerService.updateContact(customerToEdit.id, updatedCustomerData);
      await loadCustomers();
      await loadStats();
      setEditModalOpen(false);
      setCustomerToEdit(null);
    } catch (err) {
      console.error('Error updating customer:', err);
      setError('Failed to update customer. Please try again.');
    }
  };

  const handleSendReviewRequest = (customer) => {
    navigate('/reviews', { state: { customerId: customer.id } });
  };

  const handleViewReviews = (customer) => {
    navigate('/reviews', { state: { customerId: customer.id } });
  };

  const handleStageChange = async (customerId, newStage) => {
    try {
      await customerService.updateContactStage(customerId, newStage);
      await loadCustomers();
      await loadStats();
    } catch (err) {
      console.error('Error updating stage:', err);
      setError('Failed to update customer stage. Please try again.');
    }
  };

  const handleConfigureStages = () => {
    setShowStageConfig(true);
  };

  const handleStageConfigSave = async (newStages) => {
    try {
      await customerService.updatePipelineStages(newStages, currentOrganization?.id);
      setStageRefreshKey(prev => prev + 1);
      await loadCustomers();
    } catch (err) {
      console.error('Error updating stages:', err);
      setError('Failed to update pipeline stages. Please try again.');
    }
  };

  const handleDeleteCustomer = (customer) => {
    setCustomerToDelete(customer);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async (customerId) => {
    try {
      await customerService.deleteContactWithValidation(customerId);
      await loadCustomers();
      await loadStats();
      setDeleteModalOpen(false);
      setCustomerToDelete(null);
    } catch (err) {
      console.error('Error deleting customer:', err);
      setError('Failed to delete customer. Please try again.');
    }
  };

  const handleArchiveCustomer = async (customerId, reason = 'Manual archive') => {
    try {
      await customerService.archiveContact(customerId, reason);
      await loadCustomers();
      await loadStats();
    } catch (err) {
      console.error('Error archiving customer:', err);
      setError('Failed to archive customer. Please try again.');
    }
  };

  const handleOpenCustomerView = (customer) => {
    setSelectedCustomer(customer);
    setCustomerDetailModalOpen(true);
  };

  // Show loading state
  if (loading && customers.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          onSidebarToggle={handleSidebarToggle}
          sidebarExpanded={sidebarExpanded}
        />
        <EnhancedSidebar 
          expanded={sidebarExpanded}
          onToggle={handleSidebarToggle}
        />
        
        <main className={`pt-16 transition-all duration-300 ease-in-out ${
          sidebarExpanded ? 'lg:pl-64' : 'lg:pl-16'
        }`}>
          <EnhancedBreadcrumb />
          <div className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading pipeline...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onSidebarToggle={handleSidebarToggle}
        sidebarExpanded={sidebarExpanded}
      />
      <EnhancedSidebar 
        expanded={sidebarExpanded}
        onToggle={handleSidebarToggle}
      />
      
      <main className={`pt-16 transition-all duration-300 ease-in-out ${
        sidebarExpanded ? 'lg:pl-64' : 'lg:pl-16'
      }`}>
        <EnhancedBreadcrumb />
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">CRM Pipeline</h1>
              <p className="text-muted-foreground">Manage your sales pipeline and track deal progress</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button 
                variant="outline" 
                onClick={handleConfigureStages}
                className="flex items-center space-x-2"
              >
                <Icon name="Settings" size={16} />
                <span>Configure Stages</span>
              </Button>
              <Button 
                onClick={() => setShowAddModal(true)}
                className="flex items-center space-x-2"
              >
                <Icon name="Plus" size={16} />
                <span>Add Lead</span>
              </Button>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <span className="text-error font-medium">{error}</span>
              </div>
            </div>
          )}

          {/* Pipeline Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-muted">Total Leads</p>
                  <p className="text-2xl font-bold text-foreground">{stats.totalLeads || 0}</p>
                </div>
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={24} className="text-primary" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-muted">Active Pipeline</p>
                  <p className="text-2xl font-bold text-foreground">{stats.activePipeline || 0}</p>
                </div>
                <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} className="text-success" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-muted">Organizations</p>
                  <p className="text-2xl font-bold text-foreground">{stats.organizations || 0}</p>
                </div>
                <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
                  <Icon name="Building" size={24} className="text-warning" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-text-muted">Pipeline Value</p>
                  <p className="text-2xl font-bold text-foreground">
                    ${stats.totalValue?.toLocaleString() || '0'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center">
                  <Icon name="DollarSign" size={24} className="text-accent" />
                </div>
              </div>
            </div>
          </div>

          {/* CRM Pipeline */}
          <div className="mb-6">
            <CRMPipelineComponent 
              key={stageRefreshKey}
              customers={customers} 
              onStageChange={handleStageChange}
              onConfigureStages={handleConfigureStages}
              onEditCustomer={handleEditCustomer}
              onDeleteCustomer={handleDeleteCustomer}
              onArchiveCustomer={handleArchiveCustomer}
              onOpenCustomerView={handleOpenCustomerView}
              onAddCustomer={(stageId) => {
                setNewCustomerStage(stageId);
                setShowAddModal(true);
              }}
            />
          </div>
        </div>
      </main>

      {/* Modals */}
      {showAddModal && (
        <AddCustomerModal
          isOpen={showAddModal}
          onClose={() => {
            setShowAddModal(false);
            setNewCustomerStage(null);
          }}
          onSave={handleAddCustomer}
          initialStage={newCustomerStage}
        />
      )}

      {editModalOpen && customerToEdit && (
        <EditCustomerModal
          isOpen={editModalOpen}
          customer={customerToEdit}
          onClose={() => {
            setEditModalOpen(false);
            setCustomerToEdit(null);
          }}
          onSave={handleUpdateCustomer}
        />
      )}

      {showStageConfig && (
        <StageConfigModal
          isOpen={showStageConfig}
          onClose={() => setShowStageConfig(false)}
          onSave={handleStageConfigSave}
        />
      )}

      {deleteModalOpen && (
        <DeleteContactModal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setCustomerToDelete(null);
          }}
          contact={customerToDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {customerDetailModalOpen && (
        <CustomerDetailModal
          isOpen={customerDetailModalOpen}
          onClose={() => setCustomerDetailModalOpen(false)}
          customer={selectedCustomer}
          onEdit={handleEditCustomer}
          onSendReviewRequest={handleSendReviewRequest}
          onViewReviews={handleViewReviews}
        />
      )}
    </div>
  );
};

export default CRMPage; 